import { Form, Formik } from 'formik';
import {
  Checkbox,
  DatePicker,
  Grid,
  GridColumn,
  GridRow,
  ModalBottomButtons,
  Select,
  SelectOptionType,
} from '@bp/ui-components';
import { useTranslation } from 'react-i18next';
import { SingleValue } from 'react-select';
import { validationSchema } from './bildungsplattformForm.schema';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { ensureDate } from 'utils/dateCalculations';
import { showErrorToast } from 'utils/showErrorToast';
import { showSuccessToast } from 'utils/showSuccessToast';
import { graphql } from 'client/gql';
import { useMutation, useQuery } from 'urql';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { OrganizationConfigContext } from 'context/OrganizationConfigContext';
import { useContext } from 'react';

const CreateBildungsplattformsMutationDocument = graphql(`
  mutation CreateBildungsplattforms($input: [BildungsplattformCreateInput!]!) {
    createBildungsplattforms(input: $input) {
      bildungsplattforms {
        uuid
        __typename
      }
    }
  }
`);

const UpdateBildungsplattformsMutationDocument = graphql(`
  mutation UpdateBildungsplattformsInBildungsplattformForm(
    $update: BildungsplattformUpdateInput!
    $where: BildungsplattformWhere!
  ) {
    updateBildungsplattforms(update: $update, where: $where) {
      bildungsplattforms {
        uuid
        __typename
        allowStudentDirectMessages
        organizationConnection {
          __typename
          edges {
            __typename
            properties {
              __typename
              version
              notAfter
              notBefore
            }
          }
        }
      }
    }
  }
`);

const SettingsBildungsplattformFormQueryDocument = graphql(`
  query SettingsBildungsplattformFormData($profile: ID!, $uuid: ID!) {
    bildungsplattforms(where: { uuid: $uuid }) {
      uuid
      __typename
      allowStudentDirectMessages
      allowParentExcuses
      currentSchoolyear
      organizationConnection {
        __typename
        edges {
          __typename
          properties {
            __typename
            notAfter
            notBefore
            version
            contractRef
          }
        }
      }
    }
    ...PermissionCheckerFields
  }
`);

type BildungsplattformValuesType = {
  allowStudentDirectMessages: boolean;
  notAfter: string;
  notBefore: string;
  version: string;
};

type BildungsplattformFormProps = { onClose: () => void };

export const BildungsplattformForm = ({ onClose }: BildungsplattformFormProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();
  const profileUuid = pimAuthClaims.getProfile().uuid;
  const { organizationProducts } = useContext(OrganizationConfigContext);

  const bpUuid = organizationProducts?.find((d) => d.__typename === 'Bildungsplattform')?.uuid;

  const [{ data: queryData }] = useQuery({
    query: SettingsBildungsplattformFormQueryDocument,
    variables: {
      uuid: bpUuid ?? '',
      profile: profileUuid,
    },
  });

  const permissions = usePermissionChecker(queryData);

  const currentBooking = queryData?.bildungsplattforms[0];

  const [, updateBildungsplattform] = useMutation(UpdateBildungsplattformsMutationDocument);
  const [, createBildungsplattform] = useMutation(CreateBildungsplattformsMutationDocument);

  const initialValues: BildungsplattformValuesType = {
    version: currentBooking?.organizationConnection.edges[0]?.properties.version ?? '',
    allowStudentDirectMessages: currentBooking?.allowStudentDirectMessages ?? false,
    notAfter: ensureDate(currentBooking?.organizationConnection.edges[0]?.properties.notAfter).toUTCString(),
    notBefore: ensureDate(currentBooking?.organizationConnection.edges[0]?.properties.notBefore).toUTCString(),
  };

  const onSubmit = async (values: BildungsplattformValuesType) => {
    if (permissions?.canUpdateBildungsplattform({ uuid: organizationUuid })) {
      if (bpUuid === undefined) {
        const resp = await createBildungsplattform({
          input: [
            {
              allowStudentDirectMessages: values.allowStudentDirectMessages,
              organization: {
                connect: {
                  overwrite: true,
                  edge: {
                    notBefore: values.notBefore,
                    notAfter: values.notAfter,
                    version: values.version,
                  },
                  where: { node: { uuid: organizationUuid } },
                },
              },
            },
          ],
        });
        if (resp.error) {
          showErrorToast(resp.error);
        } else {
          showSuccessToast(t('common.saved'));
          onClose();
        }
      } else {
        const resp = await updateBildungsplattform({
          update: {
            allowStudentDirectMessages: values.allowStudentDirectMessages,
            organization: {
              update: {
                edge: {
                  notBefore: values.notBefore,
                  notAfter: values.notAfter,
                  version: values.version,
                },
              },
            },
          },
          where: { uuid: bpUuid },
        });
        if (resp.error) {
          showErrorToast(resp.error);
        } else {
          showSuccessToast(t('common.saved'));
          onClose();
        }
      }
    }
  };

  const versionOptions: SelectOptionType[] = [
    {
      label: t('app.pro'),
      value: 'pro',
    },
    {
      label: t('app.basic'),
      value: 'basic',
    },
    {
      label: t('app.community'),
      value: 'community',
    },
  ];

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={validationSchema}>
      {({ resetForm, setFieldValue, handleChange, values, isSubmitting, errors }) => {
        return (
          <Form>
            <Grid useFormGap>
              <GridRow>
                <GridColumn width={8}>
                  <GridRow>
                    <GridColumn width={4}>
                      <Select
                        options={versionOptions}
                        value={versionOptions.find((o) => o.value === values.version)}
                        onChange={(event) => {
                          const option: SelectOptionType | null = event as SingleValue<SelectOptionType>;
                          setFieldValue('version', option?.value);
                        }}
                        name={'version'}
                        label={t('common.version')}
                      />
                    </GridColumn>
                    <GridColumn width={4}>
                      <DatePicker
                        label={t('institution.bookedFromDate')}
                        onChange={(e) => {
                          setFieldValue('notBefore', e?.toUTCString());
                        }}
                        value={ensureDate(values.notBefore)}
                        name={'notBefore'}
                        showMonthYearDropdown
                      />
                    </GridColumn>
                    <GridColumn width={4}>
                      <DatePicker
                        label={t('institution.bookedUntilDate')}
                        onChange={(e) => {
                          setFieldValue('notAfter', e?.toUTCString());
                        }}
                        value={ensureDate(values.notAfter)}
                        name={'notAfter'}
                        showMonthYearDropdown
                      />
                    </GridColumn>
                  </GridRow>
                </GridColumn>
                <GridColumn width={4} align='end'>
                  <Checkbox
                    className='mt-3'
                    label={t('institution.allowStudentDirectMessages')}
                    name={'allowStudentDirectMessages'}
                    onChange={handleChange}
                    checked={values.allowStudentDirectMessages ?? false}
                    error={errors.allowStudentDirectMessages}
                  />
                </GridColumn>
              </GridRow>
            </Grid>

            <ModalBottomButtons
              errors={errors}
              closeButton={{
                callback: () => {
                  resetForm();
                  onClose();
                },
              }}
              isLoading={isSubmitting}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
